import { STEP_KEYS } from "@/constants/orders";
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  steps: {
    stepAcceptOrder: false,
    stepConfirmBillingAddress: false,
    stepClubCustomer: false,
    stepCreateInvoice: false,
    stepScanReceipt: false,
    stepCreateShippingLabel: false,
    stepItemShipped: false,
  },
  stepData: {
    stepClubCustomer: null,
    stepCreateInvoice: null,
    stepScanReceipt: null,
  },
  failedSteps: {},
  dialogs: {
    invoiceDialogOpen: false,
  },
  noClubNumberOrders: [],
};

const orderStepsSlice = createSlice({
  name: "orderSteps",
  initialState,
  reducers: {
    setNoClubNumber: (state, action) => {
      const { orderId } = action.payload;
      if (!state.noClubNumberOrders.includes(orderId)) {
        state.noClubNumberOrders.push(orderId);
      }
    },
    completeStep: (state, action) => {
      const { stepKey } = action.payload;
      if (state.steps.hasOwnProperty(stepKey)) {
        state.steps[stepKey] = true;
      }
    },
    incompleteStep: (state, action) => {
      const { stepKey } = action.payload;
      if (state.steps.hasOwnProperty(stepKey)) {
        state.steps[stepKey] = false;
      }
    },
    toggleStep: (state, action) => {
      const { stepKey } = action.payload;
      if (state.steps.hasOwnProperty(stepKey)) {
        state.steps[stepKey] = !state.steps[stepKey];
      }
    },
    resetAllSteps: (state) => {
      Object.keys(state.steps).forEach((key) => {
        state.steps[key] = false;
      });
    },
    setSteps: (state, action) => {
      const { steps } = action.payload;
      if (steps && typeof steps === "object") {
        Object.keys(steps).forEach((key) => {
          if (state.steps.hasOwnProperty(key)) {
            state.steps[key] = steps[key];
          }
        });
      }
    },
    setStepData: (state, action) => {
      const { stepKey, data } = action.payload;
      state.stepData[stepKey] = data; // Always set
    },
    setStepFailed: (state, action) => {
      const { stepKey, errorData } = action.payload;
      state.failedSteps[stepKey] = errorData; // {status: 500, error: msg, timestamp}
    },
    clearStepFailed: (state, action) => {
      const { stepKey } = action.payload;
      delete state.failedSteps[stepKey];
    },
    openInvoiceDialog: (state) => {
      state.dialogs.invoiceDialogOpen = true;
    },
    closeInvoiceDialog: (state) => {
      state.dialogs.invoiceDialogOpen = false;
    },
  },
});

export const {
  completeStep,
  incompleteStep,
  toggleStep,
  resetAllSteps,
  setSteps,
  setStepData,
  setStepFailed,
  clearStepFailed,
  openInvoiceDialog,
  closeInvoiceDialog,
  setNoClubNumber,
} = orderStepsSlice.actions;

export const selectNoClubNumberOrders = (state) =>
  state.orderSteps?.noClubNumberOrders || [];
export const selectAllSteps = (state) => state.orderSteps?.steps || {};
export const selectStepStatus = (stepKey) => (state) =>
  state.orderSteps?.steps?.[stepKey] || false;
export const selectIsStepCompleted = (stepKey) => (state) =>
  state.orderSteps?.steps?.[stepKey] === true;

export const selectIsStepFailed = (stepKey) => (state) =>
  !!state.orderSteps?.failedSteps?.[stepKey];

export const selectFailedStepData = (stepKey) => (state) =>
  state.orderSteps?.failedSteps?.[stepKey] || null;

export const selectAllFailedSteps = (state) =>
  state.orderSteps?.failedSteps || {};

export const selectStepData = (stepKey) => (state) =>
  state.orderSteps?.stepData?.[stepKey] || null;

export const selectAllStepData = (state) => state.orderSteps?.stepData || {};

export const selectInvoiceDialogOpen = (state) =>
  state.orderSteps?.dialogs?.invoiceDialogOpen || false;

export const selectFirstIncompleteStep = (state) => {
  const steps = state.orderSteps?.steps || {};
  for (const key of STEP_KEYS) {
    if (steps[key] === false) {
      return key;
    }
  }
  return STEP_KEYS[0];
};

export const selectCurrentStepIndex = (state) => {
  const steps = state.orderSteps?.steps || {};
  for (let i = 0; i < STEP_KEYS.length; i++) {
    if (steps[STEP_KEYS[i]] === false) {
      return i;
    }
  }
  return STEP_KEYS.length - 1;
};

export default orderStepsSlice.reducer;
